import { bookingFailureKind } from "./attempt.js";
import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useMemo, useState, type FormEvent } from "react";
import type { Tutor } from "../api.js";
import { formatDate, formatTime, londonWallClockIso } from "../format.js";
import { asLocalInput } from "./reschedule.js";
import { idempotencyKey } from "./api.js";
import {
  paymentOperationAttempt,
  type PaymentOperationAttempt,
} from "./paymentOperationModel.js";
import { paymentMethodLabel } from "./methods.js";
import {
  automaticSettlementConfirmation,
  compatibleLessonBlockProducts,
  lessonBlockExpiryIso,
  lessonBlockPaidAtProblem,
  lessonBlockRegistrationInput,
  lessonBlockSaleAttempt,
  normalizePaymentAmount,
  type AutomaticSettlementPolicy,
  type LessonBlockRegistrationInput,
} from "./lessonBlockSaleModel.js";
import {
  staffBooking,
  type LessonBlockDetail,
  type LessonBlockProduct,
  type OnSitePaymentMethod,
  type ProviderService,
  type UnpaidLesson,
} from "./staff.js";
import { notifyStudentCreditStateChanged } from "./studentCreditEvents.js";
import { AcceptedRegistrationRecovery } from "./AcceptedRegistrationRecovery.js";
import { AutomaticSettlementPolicyChoice } from "./AutomaticSettlementPolicyChoice.js";

/** A registration the server accepted, frozen for replay-only recovery. */
interface AcceptedRegistration {
  input: LessonBlockRegistrationInput;
  key: string;
  description: string;
  message: string;
}
import { useAsync } from "../useAsync.js";
import { useLessonBlockPaymentAmount } from "./useLessonBlockPaymentAmount.js";

const PAYMENT_METHODS: { value: OnSitePaymentMethod; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "card_terminal", label: "Card machine" },
  { value: "bank_transfer", label: "Bank transfer" },
];

const paymentLabel = (method: OnSitePaymentMethod): string =>
  PAYMENT_METHODS.find((item) => item.value === method)?.label ?? paymentMethodLabel(method);

const localNow = () => asLocalInput(new Date().toISOString());
const errorMessage = (cause: unknown, fallback: string) =>
  cause instanceof Error ? cause.message : fallback;

interface ProviderServicesSnapshot {
  providerId: number;
  services: ProviderService[];
}

export function MarkPaymentReceived({
  lesson,
  onCancel,
  onCompleted,
}: {
  lesson: Pick<UnpaidLesson, "portal_student_id" | "student_name" | "tutor_name" | "service_name" | "starts_at" | "amount_due" | "appointment_id" | "payment_id" | "payment_state">;
  onCancel: () => void;
  onCompleted?: (outcome: ActionOutcome) => Promise<void> | void;
}) {
  const publish = usePublishActionOutcome();
  const [uncertain, setUncertain] = useState(false);
  const [method, setMethod] = useState<OnSitePaymentMethod>("cash");
  const [paidAt, setPaidAt] = useState(localNow);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<ActionOutcome | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [hosted, setHosted] = useState(false);
  const [attempt, setAttempt] = useState<PaymentOperationAttempt | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!confirmed) return;

    let paidAtIso: string;
    try {
      const [date = "", time = ""] = paidAt.split("T");
      paidAtIso = londonWallClockIso(date, time);
    } catch (cause: unknown) {
      setProblem(errorMessage(cause, "Enter a valid London payment date and time."));
      return;
    }

    const input = {
      portal_student_id: lesson.portal_student_id,
      amount: lesson.amount_due,
      payment_method: method,
      paid_at: paidAtIso,
    };
    const nextAttempt = paymentOperationAttempt(
      attempt,
      "payment_received",
      input,
      idempotencyKey,
    );
    setAttempt(nextAttempt);
    setBusy(true);
    setProblem(null);
    setRefreshFailed(false);
    let paymentRecorded = false;
    try {
      await staffBooking.markPaymentReceived(
        lesson.appointment_id,
        lesson.payment_id,
        input,
        nextAttempt.key,
      );
      paymentRecorded = true;
      // The caller owns the authoritative list re-read. Do not close before it
      // has completed: an accepted write with a stale list is not a finished UI.
      const result: ActionOutcome = { variant: "success", title: "Payment recorded",
        description: `${lesson.student_name} · ${formatDate(lesson.starts_at)} · ${formatTime(lesson.starts_at)}`,
        details: [{ value: `£${lesson.amount_due} · ${paymentLabel(method)}` }, { value: `${lesson.service_name} with ${lesson.tutor_name}` }, { value: "Payment already received was recorded. No card payment was processed by the Hub." }] };
      await onCompleted?.(result);
      setHosted(publish(result));
      setDone(result);
    } catch (cause: unknown) {
      setRefreshFailed(paymentRecorded);
      setUncertain(!paymentRecorded && bookingFailureKind(cause) === "uncertain");
      // In particular, leave the attempt in place after a timeout or 503. The
      // next press is the same logical operation and must reuse its key.
      setProblem(
        errorMessage(
          cause,
          "The payment could not be confirmed. Retry this same payment before starting another.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  if (done && hosted) return null;
  if (done) return <ActionConfirmation {...done} reveal />;

  return (
    <form className="panel" style={{ marginTop: 14 }} onSubmit={submit}>
      <h3>Mark payment received</h3>
      {problem && (refreshFailed ? <ActionConfirmation variant="attention" title="Payment recorded" description="The payment was recorded, but the unpaid list could not be refreshed." details={[{ value: problem }, { value: "Check the lesson before recording another payment. Retrying this same operation keeps its original reference." }]} /> : uncertain ? <ActionConfirmation variant="uncertain" title="Payment outcome needs checking" description={problem} details={[{ value: "Check payments before retrying this same operation. Do not record a second payment." }]} /> : <div className="error small">{problem}</div>)}
      <table>
        <tbody>
          <tr>
            <td className="small muted">Student</td>
            <td><strong>{lesson.student_name}</strong></td>
          </tr>
          <tr>
            <td className="small muted">Lesson</td>
            <td>
              {lesson.service_name} with {lesson.tutor_name} · {formatDate(lesson.starts_at)} at{" "}
              {formatTime(lesson.starts_at)}
            </td>
          </tr>
          <tr>
            <td className="small muted">Amount due</td>
            <td><strong>£{lesson.amount_due}</strong></td>
          </tr>
          <tr>
            <td className="small muted">Current state</td>
            <td>{lesson.payment_state.replaceAll("_", " ")}</td>
          </tr>
        </tbody>
      </table>
      <div className="fields" style={{ marginTop: 12 }}>
        <label>
          <span className="small muted">Payment method</span>
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value as OnSitePaymentMethod)}
            required
          >
            {PAYMENT_METHODS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="small muted">When payment was received</span>
          <input
            type="datetime-local"
            value={paidAt}
            onChange={(event) => setPaidAt(event.target.value)}
            required
          />
        </label>
      </div>
      <div className="notice small" style={{ marginTop: 12 }}>
        <strong>Confirm:</strong> Mark {lesson.student_name}&rsquo;s {lesson.service_name} lesson
        on {formatDate(lesson.starts_at)} as paid: £{lesson.amount_due} by {paymentLabel(method).toLowerCase()}.
        This changes the booking payment state; it does not charge Stripe.
      </div>
      <label className="row small" style={{ marginTop: 12, alignItems: "start" }}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          required
        />
        <span>I have checked the exact student, lesson, amount, method and payment time.</span>
      </label>
      <div className="row" style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || !confirmed}>
          {busy ? "Recording…" : "Confirm payment received"}
        </button>
        <button type="button" className="quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function SellLessonBlock({
  studentId,
  studentName,
  canSelectProvider,
  initialProviderId,
  initialServiceName,
  ownProviderId,
  tutors,
  products,
  onCancel,
  onApply,
}: {
  studentId: string;
  studentName: string;
  canSelectProvider: boolean;
  initialProviderId?: number | null;
  initialServiceName?: string | null;
  ownProviderId: number | null;
  tutors: Tutor[];
  products: LessonBlockProduct[];
  onCancel: () => void;
  onApply: (mutation: () => Promise<LessonBlockDetail>, description: string) => Promise<void>;
}) {
  const providers = tutors.filter(
    (tutor) => !tutor.left && tutor.externalProviderId !== null,
  );
  // Prefer the lesson's tutor, then the seller's own provider, then the first
  // current tutor: a lesson whose tutor has left falls back as the form did
  // before it had a lesson to prefill from.
  const [providerId, setProviderId] = useState(
    canSelectProvider
      ? ([initialProviderId, ownProviderId].find((candidate) =>
          candidate != null && providers.some((tutor) => tutor.externalProviderId === candidate))
        ?? providers[0]?.externalProviderId ?? 0)
      : (ownProviderId ?? 0),
  );
  const {
    data: serviceSnapshot,
    error: serviceError,
    loading: serviceRequestLoading,
  } = useAsync<ProviderServicesSnapshot>(
    async () => ({
      providerId,
      services: providerId > 0 ? await staffBooking.providerServices(providerId) : [],
    }),
    [providerId],
  );
  const servicesCurrent = serviceSnapshot?.providerId === providerId;
  const services = servicesCurrent ? serviceSnapshot.services : [];
  const servicesLoading = serviceRequestLoading || (!serviceError && !servicesCurrent);
  const serviceById = useMemo(
    () => new Map(services.map((service) => [service.id, service])),
    [services],
  );
  const compatible = compatibleLessonBlockProducts(products, services);
  const [productId, setProductId] = useState("");
  const product = compatible.find((item) => item.id === productId) ??
    compatible.find((item) => serviceById.get(item.amelia_lesson_service_id)?.name === initialServiceName) ?? compatible[0];
  const service = product ? serviceById.get(product.amelia_lesson_service_id) : undefined;
  const [amount, setAmount] = useLessonBlockPaymentAmount(product, services);
  const [method, setMethod] = useState<OnSitePaymentMethod>("cash");
  const [automaticSettlementPolicy, setAutomaticSettlementPolicy] =
    useState<AutomaticSettlementPolicy>("settle_outstanding_first");
  const [paidAt, setPaidAt] = useState(localNow);
  const expiryPreview = useMemo(() => {
    try {
      const [date = "", time = ""] = paidAt.split("T");
      return lessonBlockExpiryIso(londonWallClockIso(date, time));
    } catch {
      return null;
    }
  }, [paidAt]);
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<PaymentOperationAttempt | null>(null);
  const [accepted, setAccepted] = useState<AcceptedRegistration | null>(null);
  const tutorName =
    providers.find((tutor) => tutor.externalProviderId === providerId)?.displayName ??
    `Tutor ${providerId}`;
  const serviceName = service?.name ?? product?.display_name ?? "Choose a compatible product";

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!servicesCurrent || !product || providerId <= 0 || !amount.trim() || !confirmed) return;
    const paymentAmount = normalizePaymentAmount(amount);
    if (!paymentAmount) {
      setProblem("Enter a positive payment amount with up to two decimal places.");
      return;
    }

    let paidAtIso: string;
    try {
      const [date = "", time = ""] = paidAt.split("T");
      paidAtIso = londonWallClockIso(date, time);
    } catch (cause: unknown) {
      setProblem(errorMessage(cause, "Enter a valid London payment date and time."));
      return;
    }
    const paidAtProblem = lessonBlockPaidAtProblem(paidAtIso);
    if (paidAtProblem) {
      setProblem(paidAtProblem);
      return;
    }

    const input = lessonBlockRegistrationInput({
      studentId,
      studentName,
      providerId,
      product,
      paymentAmount,
      paymentMethod: method,
      paidAt: paidAtIso,
      notes: notes.trim(),
      automaticSettlementPolicy,
    });
    const nextAttempt = lessonBlockSaleAttempt(attempt, input);
    setAttempt(nextAttempt);
    await register({
      input,
      key: nextAttempt.key,
      description: `${product.credit_quantity} ${serviceName} credits sold to ${studentName}.`,
      message: "",
    });
  }

  async function register(registration: AcceptedRegistration) {
    setBusy(true);
    setProblem(null);
    // Set the moment the server returns the block, before any follow-up read
    // or settlement check can fail.
    let registered = false;
    try {
      await onApply(
        async () => {
          const detail = await staffBooking.registerLessonBlock(registration.input, registration.key);
          registered = true;
          notifyStudentCreditStateChanged(studentId);
          return detail;
        },
        registration.description,
      );
    } catch (cause: unknown) {
      const message = errorMessage(cause, "The lesson block could not be sold. Retry this same sale before starting another.");
      if (registered) {
        // The sale exists, whether settlement or the authoritative re-read
        // failed. Freeze exactly what was accepted so later form edits can
        // never mint a new key and register a second block.
        setAccepted({ ...registration, message });
      } else {
        // Before acceptance the form keeps its attempt for an unchanged retry;
        // a failed replay from the locked view stays locked on that request.
        setProblem(message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (accepted) {
    return (
      <AcceptedRegistrationRecovery
        title="Add / sell lesson block"
        message={accepted.message}
        problem={problem}
        busy={busy}
        onRetry={() => void register(accepted)}
        onClose={onCancel}
      />
    );
  }

  return (
    <form className="panel" style={{ marginTop: 14 }} onSubmit={submit}>
      <h3>Add / sell lesson block</h3>
      {problem && <div className="error small">{problem}</div>}
      <div className="fields">
        <label>
          <span className="small muted">Student</span>
          <input value={studentName} readOnly />
        </label>
        {canSelectProvider ? (
          <label>
            <span className="small muted">Tutor</span>
            <select
              value={providerId}
              onChange={(event) => {
                setProviderId(Number(event.target.value));
                setProductId("");
                setConfirmed(false);
                setAttempt(null);
                setProblem(null);
              }}
              required
            >
              {providers.map((tutor) => (
                <option key={tutor.id} value={tutor.externalProviderId ?? ""}>
                  {tutor.displayName}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="small">
            <span className="muted">Tutor: </span>
            {providerId > 0 ? tutorName : "Your booking-provider account is not linked"}
          </p>
        )}
        <label>
          <span className="small muted">Configured block product</span>
          <select
            value={product?.id ?? ""}
            onChange={(event) => {
              setProductId(event.target.value);
              setConfirmed(false);
              setAttempt(null);
              setProblem(null);
            }}
            disabled={!servicesCurrent || servicesLoading || compatible.length === 0}
            required
          >
            {compatible.map((item) => (
              <option value={item.id} key={item.id}>
                {item.display_name} · {serviceById.get(item.amelia_lesson_service_id)?.name ?? "Lesson"}
              </option>
            ))}
          </select>
        </label>
        {serviceError && <div className="error small">{serviceError}</div>}
        {!servicesLoading && !serviceError && compatible.length === 0 && (
          <p className="small muted">No compatible lesson-block product is available for this tutor.</p>
        )}
        <p className="small">
          <span className="muted">Normal lesson service / credits: </span>
          {serviceName} · {product?.credit_quantity ?? "—"} credits
        </p>
        <label>
          <span className="small muted">Payment amount</span>
          <input
            inputMode="decimal"
            pattern="[0-9]+([.][0-9]{1,2})?"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            disabled={!servicesCurrent || servicesLoading || !product}
            required
          />
        </label>
        <label>
          <span className="small muted">Payment method</span>
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value as OnSitePaymentMethod)}
            required
          >
            {PAYMENT_METHODS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="small muted">When payment was received</span>
          <input
            type="datetime-local"
            value={paidAt}
            onChange={(event) => {
              setPaidAt(event.target.value);
              setConfirmed(false);
              setAttempt(null);
              setProblem(null);
            }}
            required
          />
          {expiryPreview && (
            <span className="small muted">
              Credits expire on {formatDate(expiryPreview)}.
            </span>
          )}
        </label>
        <label>
          <span className="small muted">Note (optional)</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={2000}
            style={{ width: "100%" }}
          />
        </label>
      </div>
      <AutomaticSettlementPolicyChoice
        name="lesson-block-allocation"
        value={automaticSettlementPolicy}
        onChange={(value) => {
          setAutomaticSettlementPolicy(value);
          setConfirmed(false);
          setAttempt(null);
          setProblem(null);
        }}
        disabled={busy || !product}
      />
      <div className="notice small" style={{ marginTop: 12 }}>
        <strong>Confirm:</strong> Add {product?.credit_quantity ?? "—"} × {serviceName} credits
        for {studentName} with {tutorName}. Payment £{amount || "0.00"} by {paymentLabel(method).toLowerCase()}.{" "}
        {expiryPreview ? <>Credits expire on {formatDate(expiryPreview)}.</> : <>Check the payment date to see the expiry.</>}
        {" "}{automaticSettlementConfirmation(automaticSettlementPolicy, product?.credit_quantity ?? 0)}
        {" "}Stripe is not a manual payment option.
      </div>
      <label className="row small" style={{ marginTop: 12, alignItems: "start" }}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          disabled={!servicesCurrent || servicesLoading || !product}
          required
        />
        <span>I have checked the exact student, tutor, product, credits, amount, method, note, expiry and credit allocation.</span>
      </label>
      <div className="row" style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || !confirmed || !servicesCurrent || servicesLoading || !product}>
          {busy ? "Selling…" : "Confirm lesson block sale"}
        </button>
        <button type="button" className="quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
